import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid, Modal, Space, Spin, Typography, Tabs, Button } from 'antd';
import { Notice } from '../../components/common/Notice';
import { engagementService } from '../../services/engagement.service';
import {
  ParticipantEngagementCycleResponse,
  ParticipantReferralItem,
} from '../../types/engagement.types';
import EngagementPointsCard from '../../components/participant/EngagementPointsCard';
import EngagementEventTimeline from '../../components/participant/EngagementEventTimeline';
import EngagementReferralLinkCard from '../../components/participant/EngagementReferralLinkCard';
import EngagementReferralHighlight from '../../components/participant/EngagementReferralHighlight';
import EngagementReferralTable from '../../components/participant/EngagementReferralTable';
import { useOpenGraph } from '../../hooks/useOpenGraph';
import { useEngagementTickets, useCycleHistory } from '../../hooks/useEngagementTickets';
import { useLotteryResults } from '../../hooks/useLotteryResults';
import LotteryTicketList from '../../components/engagement/LotteryTicketList';
import CycleHistorySection from '../../components/engagement/CycleHistorySection';
import LotteryResultsCard from '../../components/engagement/LotteryResultsCard';
import RegulationModal from '../../components/engagement/RegulationModal';
import { useQuery } from '@tanstack/react-query';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const BRAND_INDIGO = '#2E2B81';

const VALID_TABS = ['points', 'tickets', 'history', 'referrals'] as const;

const ParticipantEngagementPage = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { hash, search } = useLocation();

  const resolveTabFromLocation = () => {
    const fromQuery = new URLSearchParams(search).get('tab');
    if (fromQuery && (VALID_TABS as readonly string[]).includes(fromQuery)) {
      return fromQuery;
    }
    const fromHash = hash.replace('#', '');
    return (VALID_TABS as readonly string[]).includes(fromHash) ? fromHash : 'points';
  };

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>(resolveTabFromLocation);
  const [error, setError] = useState('');
  const [eventPage, setEventPage] = useState(1);
  const [engagementData, setEngagementData] = useState<ParticipantEngagementCycleResponse | null>(null);
  const [referrals, setReferrals] = useState<ParticipantReferralItem[]>([]);
  const [selectedResultsCycleKey, setSelectedResultsCycleKey] = useState<string | null>(null);
  const [regulationOpen, setRegulationOpen] = useState(false);

  const { data: ticketsData } = useEngagementTickets();
  const { data: historyData } = useCycleHistory();
  const { data: lotteryResults } = useLotteryResults(selectedResultsCycleKey ?? '');
  const { data: regulation, isLoading: loadingRegulation } = useQuery({
    queryKey: ['engagement', 'regulation'],
    queryFn: () => engagementService.getRegulation(),
  });

  useOpenGraph({
    title: 'Pontuação de Engajamento - PePoll',
    description:
      'Acompanhe seus pontos, histórico de eventos e indicações no programa de engajamento da PePoll.',
    url: 'https://app.pepoll.org/engagement',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        const [cycle, referralList] = await Promise.all([
          engagementService.getCurrentCycle({ page: eventPage, pageSize: 10 }),
          engagementService.listReferrals({ page: 1, pageSize: 20 }),
        ]);

        setEngagementData(cycle);
        setReferrals(referralList.items ?? []);
      } catch {
        setError('Não foi possível carregar os dados do programa de engajamento.');
        setEngagementData(null);
        setReferrals([]);
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [eventPage]);

  useEffect(() => {
    setActiveTab(resolveTabFromLocation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, hash]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 384 }}>
        <Spin size="large" tip="Carregando dados de engajamento..." />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1152, margin: '0 auto', padding: isMobile ? '8px 0' : '32px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ color: BRAND_INDIGO, marginBottom: 4 }}>
            Pontuação de Engajamento
          </Title>
          <Text type="secondary">
            Veja seus pontos do ciclo atual, histórico de ações e status das suas indicações.
          </Text>
        </div>
        <Button onClick={() => setRegulationOpen(true)}>Regulamento</Button>
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {error && <Notice message={error} type="warning" showIcon />}

        {engagementData && (
          <EngagementPointsCard data={engagementData} />
        )}

        {engagementData && (
          <EngagementReferralHighlight referralLink={engagementData.referralLink} />
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'points',
              label: 'Pontos e eventos',
              children: engagementData ? (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <EngagementEventTimeline
                    events={engagementData.recentEvents}
                    page={engagementData.recentEventsPagination.page}
                    pageSize={engagementData.recentEventsPagination.pageSize}
                    total={engagementData.recentEventsPagination.total}
                    onPageChange={setEventPage}
                  />
                </Space>
              ) : null,
            },
            {
              key: 'tickets',
              label: 'Meus bilhetes',
              children: (
                <LotteryTicketList
                  events={ticketsData?.events ?? []}
                  minimumReached={ticketsData?.minimumReached ?? false}
                  minimumPrizePoints={ticketsData?.cycle?.minimumPrizePoints}
                  totalPoints={ticketsData?.totalPoints}
                />
              ),
            },
            {
              key: 'history',
              label: 'Histórico de ciclos',
              children: (
                <CycleHistorySection
                  cycles={historyData?.cycles ?? []}
                  onViewResults={(key) => setSelectedResultsCycleKey(key)}
                />
              ),
            },
            {
              key: 'referrals',
              label: 'Indicações',
              children: engagementData ? (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <EngagementReferralLinkCard referralLink={engagementData.referralLink} />
                  <EngagementReferralTable referrals={referrals} />
                </Space>
              ) : null,
            },
          ]}
        />
      </Space>

      <RegulationModal
        open={regulationOpen}
        onClose={() => setRegulationOpen(false)}
        regulation={regulation ?? null}
        loading={loadingRegulation}
      />

      <Modal
        open={!!selectedResultsCycleKey}
        onCancel={() => setSelectedResultsCycleKey(null)}
        footer={null}
        width={640}
        destroyOnHidden
      >
        {lotteryResults && <LotteryResultsCard results={lotteryResults} />}
        {!lotteryResults && <Spin style={{ display: 'block', textAlign: 'center', padding: 32 }} />}
      </Modal>
    </div>
  );
};

export default ParticipantEngagementPage;